import { FunctionComponent, useEffect, useState } from "react";
import {
  defaultPurchaseValueV2,
  PurchaseVoucherModelV2,
} from "../../models/purchase_model";
import { UTCToLocalDate } from "../../../../utils/date_util";
import moment from "moment";
import {
  InwardModel,
  ItemMovementModelV2,
} from "../../../inventory/models/inventory_voucher";
import {
  ModalAlignment,
  ModalType,
} from "../../../../context_providers/modal/modal_context";
import Icon from "@mdi/react";
import { mdiAttachment, mdiChevronLeft, mdiRefresh } from "@mdi/js";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { useHistory, useLocation, useParams } from "react-router-dom";
import PartySearchField from "../../parties/party_search_field";
import DrawerWidget from "../../../../context_providers/modal/drawer_widget";
import {
  PoItemV2,
  PurchaseOrderModelV2,
} from "../../../purchase_order/model/purchase_order_model";
import { formToObject } from "../../../../utils/formDataToJson";
import { currencies } from "../../models/party_model";
import ItemRowV2 from "./components/item_row_v2";
import InwardAttachmentSection from "./components/inward_attachment_widget";
import WarehouseSearchField from "../../../inventory/warehouse/warehouse_search_field";
import {
  CreateInwardAndVoucher,
  GetInwardsFromPO,
  GetPurchaseOrder,
  GetServiceInwardsFromPO,
} from "./service/inward_repo";
import LoadingWidget from "../../../../context_providers/modal/loader";
import ModalDialog from "../../../../ui/new_components/common/modal";
import PurchaseVoucherQRCodeModal from "./components/purchase_voucher_item_qrcode_modal";
import "../../../../styles/responsive_shrink.css";

interface PurchaseVoucherProps {
  location?: any;
  type?: string;
}

const PurchaseVoucher: FunctionComponent<PurchaseVoucherProps> = (props) => {
  const { id } = useParams<{ id: string }>();
  const navaigation = useHistory();
  const query = new URLSearchParams(props.location.search);
  const po_no = query.get("po_no");
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const [poData, setPoData] = useState<PurchaseOrderModelV2>();
  const [voucherData, setVoucherData] = useState<PurchaseVoucherModelV2>(
    defaultPurchaseValueV2,
  );
  const [existingItems, setExistingItems] = useState<InwardModel[]>([]);
  const [newItems, setNewItems] = useState<ItemMovementModelV2[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [showAttachment, setShowAttachment] = useState(false);
  const [attachmentKey, setAttachmentKey] = useState(Math.random() * 10000);
  const [showQRCodeModal, setQRCodeModal] = useState<boolean>(false);
  const [projectMap, setProjectMap] = useState<
    Record<string, { project_id: string; project: any }>
  >({});
  const location = useLocation<{ type?: string }>();
  const type = location.state?.type;
  const [poItems, setPoItems] = useState([]);

  const onSubmit = async () => {
    if (!newItems || newItems.length === 0) {
      return showToast({
        type: ToastType.error,
        text: "Inward items required for Inward!",
      });
    }
    setLoading(true);
    let form = new FormData();
    form.append("voucherData", JSON.stringify(voucherData));
    form.append("newItems", JSON.stringify(newItems));

    if (files && files.length) {
      files.forEach((f) => {
        form.append("attachment", f);
      });
    }

    const res = await CreateInwardAndVoucher(form);

    if (res.success) {
      setLoading(false);

      if (res.data.id!) {
        setVoucherData(res.data);
        navaigation.goBack();
        showToast({ type: ToastType.success, text: res.message });
      }
    } else showToast({ type: ToastType.error, text: res.error });
    setLoading(false);
  };

  useEffect(() => {
    if (po_no) {
      getPoData(po_no);
    }
  }, []);

  // Fetch PO data and project data
  const getPoData = async (id: string) => {
    setLoading(true);
    try {
      const res = await GetPurchaseOrder(id);
      if (res.success) {
        if (!res.data.vendor_currency) {
          res.data.vendor_currency = currencies.rupee;
          res.data.vendor_currency_to_inr = 1;
        }
        if (type === "Service") {
          const filteredItems =
            props.type === "Service"
              ? (res.data.items || []).filter(
                  (i: PoItemV2) => i.item?.type === "Service",
                )
              : res.data.items || [];

          setPoItems(filteredItems);
        } else {
          setPoItems(res.data.items || []);
        }

        setVoucherData((o) => ({
          ...o,
          due_date: res.data.delivery_date,
          date: res.data.delivery_date,
          party_id: res.data.party.id,
          party_detail: res.data.party,
          warehouse: res.data.warehouse,
          warehouse_id: res.data.warehouse_id,
          order_detail: {
            ...(o.order_detail || {}), // Ensure order_detail exists
            order_id: "",
            payment_terms: "",
            other_ref: "",
            delivery_terms: "",
            po_no: res.data.po_no,
          },
        }));

        setPoData(res.data);
        setLoading(false);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching PO data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getInwards = async (po_no: string) => {
    setLoading(true);
    try {
      let res;
      if (type === "Service") {
        res = await GetServiceInwardsFromPO(po_no);
      } else res = await GetInwardsFromPO(po_no);

      if (res.success) {
        setExistingItems(res.data);
        setLoading(false);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching Inward data.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (poData) {
      getInwards(poData.po_no);
    }
  }, [poData]);

  const uploadFile = async ({
    file,
    category,
    removeSelectedFile,
    setShowUpload,
  }: {
    file: File;
    category: string;
    removeSelectedFile: () => void;
    setShowUpload: (v: boolean) => void;
  }) => {
    try {
      if (!file) {
        showToast({
          type: ToastType.warning,
          text: "Please select a file",
        });
        return;
      }

      setLoading(true);

      // Check if the file already exists in state
      setFiles((prevFiles) => {
        const isDuplicate = prevFiles.some(
          (existingFile) =>
            existingFile.name === file.name && existingFile.size === file.size,
        );

        if (isDuplicate) {
          showToast({
            type: ToastType.warning,
            text: "File already uploaded",
          });
          return prevFiles; // Return previous state to prevent duplicate append
        }

        return [...prevFiles, file]; // Append new file
      });

      // Perform additional actions
      removeSelectedFile();
      setShowUpload(false);
      setAttachmentKey(Math.random() * 1000);

      setLoading(false);
    } catch (error: any) {
      showToast({
        type: ToastType.error,
        text: error.message,
      });
      setLoading(false);
    }
  };

  const mapProjectsToItemMap = (
    existingItems: any[],
    poItems: any[],
  ): Record<string, { project_id: string; project: any }> => {
    if (!existingItems || !poItems) return {};

    const poMap: Record<string, { project_id: string; project: any }> = {};
    poItems.forEach((po) => {
      if (po.item?.item_code) {
        poMap[po.item.item_code] = {
          project_id: po.project_id,
          project: po.project,
        };
      }
    });

    return poMap;
  };

  useEffect(() => {
    if (existingItems && poItems) {
      const map = mapProjectsToItemMap(existingItems, poItems);
      setProjectMap(map);
    }
  }, [existingItems, poItems]);

  return (
    <>
      {showQRCodeModal && (
        <ModalDialog
          onClose={() => setQRCodeModal(false)}
          show={showQRCodeModal}
          title="GRN Item QR Codes"
        >
          <div className="p-4 sm:p-5 w-full md:w-[50vw] max-w-full h-[80vh] overflow-auto">
            <PurchaseVoucherQRCodeModal data={existingItems} />
          </div>
        </ModalDialog>
      )}
      {showAttachment && (
        <DrawerWidget
          data={{
            id: 1,
            title: "Attachments",
            type: ModalType.drawer,
            alignment: ModalAlignment.right,
            container: (
              <InwardAttachmentSection
                key={attachmentKey}
                attachments={files}
                onSubmit={uploadFile}
                onDelete={(fileToDelete: File) => {
                  setFiles((prevFiles) =>
                    prevFiles.filter((file) => file !== fileToDelete),
                  );
                }}
                onSelect={() => {}}
              />
            ),
          }}
          onClose={function (): void {
            setShowAttachment(false);
          }}
        ></DrawerWidget>
      )}
      {id && (
        <div className="fixed bottom-3 right-3">
          <button
            title="Attachments"
            onClick={() => setShowAttachment(true)}
            className="  rounded-full p-2  hover:scale-105 transform duration-150 bg-green-400 text-white   shadow cursor-pointer flex items-center justify-center"
          >
            <Icon
              path={mdiAttachment}
              size={1}
              className="hover:scale-105 transform -rotate-45"
            ></Icon>
          </button>
        </div>
      )}
      {showOrderDetail && (
        <DrawerWidget
          data={{
            id: 1,
            title: "Order detail",
            type: ModalType.drawer,
            alignment: ModalAlignment.right,
            container: (
              <>
                <div className="p-5 text-xs">
                  <div className="flex flex-col items-start  w-full">
                    <h2 className="text-sm font-semibold">Party</h2>
                    <PartySearchField
                      // show_add={true}
                      value={poData?.party}
                      onSelect={(d) => {
                        setVoucherData((o) => ({
                          ...o,
                          party_detail: d,
                        }));
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-2  my-2 gap-1">
                    <div className=" font-semibold text-gray-500">Party</div>
                    <div className="">: {poData?.party?.name}</div>
                    <div className=" font-semibold text-gray-500">Address</div>
                    <div className="">
                      : {poData?.party?.mail_detail.address.city},
                      {poData?.party?.mail_detail.address.state},
                      {poData?.party?.mail_detail.address.country}-
                      {poData?.party?.mail_detail.address.pincode},
                    </div>
                    <div className=" font-semibold text-gray-500">
                      Contact name
                    </div>
                    <div className="">
                      : {poData?.party?.mail_detail.contact_name}
                    </div>
                    <div className=" font-semibold text-gray-500">
                      Contact mobile
                    </div>
                    <div className="">
                      : {poData?.party?.mail_detail.contact_mobile}
                    </div>
                    <div className=" font-semibold text-gray-500">
                      Contact email
                    </div>
                    <div className="">
                      : {poData?.party?.mail_detail.contact_email}
                    </div>
                  </div>

                  <hr />
                  <h2 className="text-sm font-semibold">Order details</h2>
                  <div className="grid grid-cols-1  my-2 gap-1">
                    <div className="grid grid-cols-4 items-center">
                      <label
                        htmlFor=""
                        className=" font-semibold text-gray-500"
                      >
                        Order ref
                      </label>{" "}
                      <input
                        type="text"
                        className="col-span-3 focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full border"
                        value={voucherData.order_detail?.order_id}
                        onChange={(e) => {
                          setVoucherData((o) => ({
                            ...o,
                            order_detail: {
                              ...o.order_detail!,
                              order_id: e.target.value,
                            },
                          }));
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center">
                      <label
                        htmlFor=""
                        className=" font-semibold text-gray-500"
                      >
                        Delivery terms
                      </label>{" "}
                      <textarea
                        className="col-span-3 focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full border"
                        value={voucherData.order_detail?.delivery_terms}
                        onChange={(e) => {
                          setVoucherData((o) => ({
                            ...o,
                            order_detail: {
                              ...o.order_detail!,
                              delivery_terms: e.target.value,
                            },
                          }));
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center">
                      <label
                        htmlFor=""
                        className=" font-semibold text-gray-500"
                      >
                        Payment terms
                      </label>{" "}
                      <textarea
                        className="col-span-3 focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full border"
                        value={voucherData.order_detail?.payment_terms}
                        onChange={(e) => {
                          setVoucherData((o) => ({
                            ...o,
                            order_detail: {
                              ...o.order_detail!,
                              payment_terms: e.target.value,
                            },
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>
              </>
            ),
          }}
          onClose={function (): void {
            setShowOrderDetail(false);
          }}
        ></DrawerWidget>
      )}
      <div className="mobile-shrink mt-2 px-2 md:px-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-2">
          <div className="flex flex-row">
            <button
              className="  flex  items-center p-2  hover:text-gray-700  font-bold text-lg"
              onClick={() => {
                navaigation.goBack();
              }}
            >
              <Icon path={mdiChevronLeft} className="h-6 w-6 font-bold"></Icon>{" "}
              Back
            </button>
            <div className="text-xl font-bold border-l-4 border-myPrimaryColor p-2">
              {type === "Service"
                ? "Service Received Note"
                : "Goods Received Note"}
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <label htmlFor="" className="text-sm font-semibold text-gray-500">
              Date
            </label>{" "}
            <input
              type="date"
              className=" focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full text-sm border"
              value={
                voucherData.date
                  ? moment(voucherData.date).format("YYYY-MM-DD")
                  : ""
              }
              onChange={(e) => {
                const date = UTCToLocalDate(e.target.value)!;
                setVoucherData((o) => ({
                  ...o,
                  date: date,
                }));
              }}
            />
          </div>
        </div>
        <div className="flex justify-end m-2">
          {" "}
          <button
            type="button"
            onClick={() => {
              setShowAttachment(true);
            }}
            className="px-10 py-1 rounded-md bg-myPrimaryColor text-white text-sm "
          >
            Attachment
          </button>
        </div>
        <div className="bg-white rounded-lg p-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 md:gap-x-12 gap-y-2 items-start justify-start w-full md:w-2/3 lg:w-1/2">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const d = formToObject(new FormData(e.currentTarget));
                if (d["po_no"]) {
                  if (existingItems!.length > 0) {
                    const confirm = window.confirm(
                      "Are you sure to refresh po data?",
                    );
                    if (!confirm) {
                      return;
                    }
                  }
                  getPoData(d["po_no"]);
                }
              }}
              className="grid grid-cols-1 items-center"
            >
              <label
                htmlFor="invoice_no"
                className="text-sm font-semibold text-gray-500"
              >
                PO number
              </label>

              <div className="flex gap-1 col-span-3 items-center">
                <input
                  disabled={true}
                  value={voucherData.order_detail?.po_no}
                  onChange={(e) => {
                    setVoucherData((o) => ({
                      ...o,
                      order_detail: {
                        ...o.order_detail!,
                        po_no: e.target.value,
                      },
                    }));
                  }}
                  required
                  placeholder="Po number"
                  name="po_no"
                  type="text"
                  className="focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full border"
                />
                <button
                  title="Fetch po details"
                  className="rounded-full p-1 border"
                >
                  <Icon
                    path={mdiRefresh}
                    size={1}
                    className="hover:scale-110 transform"
                  ></Icon>
                </button>
              </div>
            </form>
            <div>
              <div className="grid grid-cols-1 items-center">
                <label
                  htmlFor=""
                  className="text-sm font-semibold text-gray-500"
                >
                  Delivery Date
                </label>{" "}
                <input
                  type="date"
                  className=" focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full text-sm border"
                  value={
                    voucherData.due_date
                      ? moment(voucherData.due_date).format("YYYY-MM-DD")
                      : "--"
                  }
                  onChange={(e) => {
                    const date = UTCToLocalDate(e.target.value)!;
                    setVoucherData((o) => ({
                      ...o,
                      due_date: date,
                    }));
                  }}
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 md:gap-x-12 gap-y-2 mt-3 items-start justify-start">
            <div className="grid grid-cols-1 items-center">
              <label htmlFor="" className="text-sm font-semibold text-gray-500">
                Vendor
              </label>
              <div className="col-span-3">
                <PartySearchField
                  disabled={true}
                  key={Math.random() * 10000}
                  value={poData?.party ? poData.party : undefined}
                  onSelect={(d) => {
                    setVoucherData((o) => ({
                      ...o,
                      // party_detail: party,
                    }));
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 items-center">
              <label htmlFor="" className="text-sm font-semibold text-gray-500">
                Warehouse
              </label>
              <div className="col-span-3">
                <WarehouseSearchField
                  // disabled={true}
                  key={Math.random() * 10000}
                  value={
                    voucherData?.warehouse ? voucherData.warehouse : undefined
                  }
                  onSelect={(d) => {
                    setVoucherData((o) => ({
                      ...o,
                      // party_detail: party,
                      warehouse: d,
                      warehouse_id: d.id,
                    }));
                  }}
                />
              </div>
            </div>
          </div>{" "}
        </div>
        <div className="relative">
          <div className="mt-3 mb-3 border-b border-gray-300 text-sm font-bold">
            Item details
          </div>

          {/* Table grid */}
          <div className="bg-white overflow-x-auto max-h-[60vh] rounded">
            <div className="min-w-[960px] flex flex-col gap-1">
              <div className="grid grid-cols-12 bg-myPrimaryColor text-white font-semibold text-sm text-center rounded-t">
                <div className="py-1 px-2 col-span-1">S.No</div>
                <div className="py-1 px-2 col-span-1">Item Code</div>
                <div className="py-1 px-2 col-span-1">Item</div>
                <div className="py-1 px-2 col-span-1">Unit Measure</div>
                <div className="py-1 px-2 col-span-1">Total PO Qty</div>
                <div className="py-1 px-2 col-span-1">
                  Previously Received QTY
                </div>
                <div className="py-1 px-2 col-span-1">Received QTY</div>
                <div className="py-1 px-2 col-span-1">Project Name</div>
                <div className="py-1 px-2 col-span-1">Cost Center Name</div>
                <div className="py-1 px-2 col-span-1">Cost Center ID</div>
                <div className="py-1 px-2 col-span-1">Device IDs</div>
                <div className="py-1 px-2 col-span-1">Actions</div>
              </div>

              {existingItems?.map((item, i) => {
                let tempItem: any = item;
                const projectInfo = projectMap[tempItem.item_code]; // Only for this item
                return (
                  <ItemRowV2
                    max_qty={
                      voucherData.order_detail?.po_no ? item.unit_no : undefined
                    }
                    index={i + 1}
                    key={item.id}
                    onDelete={(d) => {
                      // setVoucherData((o) => {
                      //   const items = [...(o.items || [])].filter(
                      //     (i) => i.id !== d.id
                      //   );
                      //   let sub_total = items.reduce(
                      //     (pv, val) => pv + val.price * val.bill_unit_no,
                      //     0
                      //   );
                      // });
                    }}
                    onSubmit={function (data: ItemMovementModelV2): void {
                      setNewItems((o) => {
                        let updatedList: ItemMovementModelV2[] = [...newItems];

                        const existingItemIndex = newItems.findIndex(
                          (it: ItemMovementModelV2) => it.id === data.id,
                        );

                        // there is an existingItem in the list which we need to replace
                        if (existingItemIndex !== -1) {
                          updatedList[existingItemIndex] = data;
                        }
                        // we dont have any same item exisiting we need to add this in our updated list
                        else {
                          updatedList = [...(o || []), data];
                        }

                        updatedList = updatedList.filter(
                          (item) => item.received_unit_no !== 0,
                        );

                        return updatedList;
                      });
                    }}
                    item={item}
                    edit={false}
                    disabled={false}
                    projectDetails={projectInfo}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 justify-end px-2 md:px-6 py-2">
        <button
          type="button"
          onClick={() => setQRCodeModal(true)}
          className="px-6 sm:px-10 py-1 rounded-md bg-myPrimaryColor text-white text-sm "
        >
          QR Code
        </button>
        <button
          type="button"
          onClick={() => {
            onSubmit();
          }}
          className="px-6 sm:px-10 py-1 rounded-md bg-myPrimaryColor text-white text-sm "
        >
          Submit
        </button>
        <button
          type="button"
          onClick={() => {
            navaigation.goBack();
          }}
          className="px-6 sm:px-10 py-1 rounded-md bg-white border text-black text-sm "
        >
          Quit
        </button>
      </div>
      {loading && <LoadingWidget />}
    </>
  );
};

export default PurchaseVoucher;
